import { MutableRefObject } from 'react';
import { scrollToNextItem, scrollToPrevItem, setElementActive } from './utils';

export const createTransformScroll = (
  scrollerEle: Element | null,
  currIndexRef: MutableRefObject<number>,
  autoWidth: boolean,
) => {
  let disabled = false;
  return (event: WheelEvent) => {
    if (disabled || !event.deltaY || !scrollerEle) return;

    event.preventDefault();

    currIndexRef.current =
      event.deltaY > 0
        ? scrollToNextItem(scrollerEle, currIndexRef.current, autoWidth)
        : scrollToPrevItem(scrollerEle, currIndexRef.current, autoWidth);

    disabled = true;
    setTimeout(() => {
      disabled = false;
    }, 500);
  };
};

export const createOnClickHandler = (
  contentEle: Element,
  currIndexRef: MutableRefObject<number>,
  autoWidth = false,
) => {
  return (clickEvent: Event) => {
    // Only change active item if user clicks on an item
    if (clickEvent.target === contentEle) return;

    setElementActive(contentEle.querySelector('.active'), false);
    const item = (clickEvent.target as Element).closest('.item');
    if (!item) return;

    setElementActive(item);
    const itemIndex = Array.from(contentEle.children).indexOf(item);
    currIndexRef.current = itemIndex;
    if (autoWidth) contentEle.setAttribute('style', `width: ${item?.clientWidth}px`);
    item?.scrollIntoView();
  };
};

export const createOnMouseMoveHandler = (
  contentEle: Element,
  currIndexRef: MutableRefObject<number>,
  autoWidth = false,
): ((e: any) => void) => {
  return (mouseEvent: MouseEvent) => {
    setElementActive(contentEle.querySelector('.active'), false);

    let childWidths = Array.from(contentEle.children).map((child: Element) => child.clientWidth);

    let scrollLeft = contentEle.scrollLeft;
    while (scrollLeft > 0 && childWidths.length >= 0) {
      scrollLeft -= childWidths[0];
      childWidths = childWidths.splice(1);
    }

    const item = (mouseEvent.target as Element).closest('.item');
    if (!item) return;

    setElementActive(item);
    const itemIndex = Array.from(contentEle.children).indexOf(item);
    currIndexRef.current = itemIndex;
    if (autoWidth) contentEle.setAttribute('style', `width: ${item?.clientWidth}px`);
    item?.scrollIntoView();
  };
};

export const createTouchMoveHandler = (
  _scrollerEle: Element | null,
  _currIndexRef: MutableRefObject<number>,
  _autoWidth: boolean,
  touchRef: MutableRefObject<any>,
) => {
  return (e: TouchEvent) => {
    e.preventDefault();
    touchRef.current = {
      ...touchRef.current,
      end: e.touches[0].clientX,
    };
  };
};

export const createTouchStartHandler = (
  _scrollerEle: Element | null,
  _currIndexRef: MutableRefObject<number>,
  _autoWidth: boolean,
  touchRef: MutableRefObject<any>,
) => {
  return (e: TouchEvent) => {
    e.preventDefault();
    touchRef.current = {
      ...touchRef.current,
      start: e.touches[0].clientX,
      target: e.target,
    };
  };
};

export const createTouchEndHandler = (
  scrollerEle: Element | null,
  currIndexRef: MutableRefObject<number>,
  autoWidth: boolean,
  touchRef: MutableRefObject<any>,
) => {
  return (e: TouchEvent) => {
    e.preventDefault();
    const { start, end, target } = touchRef.current;

    if (!start || !end) return target?.click();

    const diff = start - end;
    touchRef.current = {};

    if (diff > 5) {
      currIndexRef.current = scrollToNextItem(scrollerEle, currIndexRef.current, autoWidth);
      return;
    }

    if (diff < -5) {
      currIndexRef.current = scrollToPrevItem(scrollerEle, currIndexRef.current, autoWidth);
      return;
    }

    target.click();
  };
};

export const setupEventHandlers = (
  contentEle: Element | null,
  currIndexRef: MutableRefObject<number>,
  autoWidth = false,
  touchRef: MutableRefObject<any>,
) => {
  if (!contentEle) return;

  const eventListeners: {
    type: string;
    createHandlerFn: (...any: any[]) => (e: any) => void;
  }[] = [
    { type: 'wheel', createHandlerFn: createTransformScroll },
    { type: 'click', createHandlerFn: createOnClickHandler },
    {
      type: 'touchmove',
      createHandlerFn: createTouchMoveHandler,
    },
    {
      type: 'touchstart',
      createHandlerFn: createTouchStartHandler,
    },
    {
      type: 'touchend',
      createHandlerFn: createTouchEndHandler,
    },
  ];

  // Remove event listeners. Otherwise handler will be called multiple times
  eventListeners.forEach((el) =>
    contentEle?.removeEventListener(
      el.type,
      el.createHandlerFn(contentEle, currIndexRef, autoWidth, touchRef),
    ),
  );

  // Add event listeners
  eventListeners.forEach((el) =>
    contentEle?.addEventListener(
      el.type,
      el.createHandlerFn(contentEle, currIndexRef, autoWidth, touchRef),
    ),
  );
};
